import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import useWebSocket from 'react-use-websocket';
import {
    dismissToast,
    getToastTypeLabel,
    pushToast,
    subscribeToToasts,
} from './toastService';
import './Toast.css';

function ToastIcon({ type }) {
    if (type === 'success') {
        return (
            <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
        );
    }

    if (type === 'error') {
        return (
            <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
        );
    }

    if (type === 'warning') {
        return (
            <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
        );
    }

    return (
        <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
    );
}

function ToastItem({ toast, onDismiss }) {
    const [isLeaving, setIsLeaving] = useState(false);

    const handleDismiss = useCallback(() => {
        setIsLeaving(true);
        window.setTimeout(() => onDismiss(toast.id), 200);
    }, [onDismiss, toast.id]);

    useEffect(() => {
        if (!toast.duration) return undefined;

        const timer = window.setTimeout(handleDismiss, toast.duration);
        return () => window.clearTimeout(timer);
    }, [handleDismiss, toast.duration, toast.id]);

    return (
        <div
            className={`app-toast app-toast--${toast.type}${isLeaving ? ' app-toast--leaving' : ''}`}
            role="alert"
            aria-live="assertive"
        >
            <span className="app-toast__icon">
                <ToastIcon type={toast.type} />
            </span>
            <div className="app-toast__body">
                <span className="app-toast__label">{getToastTypeLabel(toast.type)}</span>
                <p className="app-toast__message">{toast.text}</p>
            </div>
            <button
                type="button"
                className="app-toast__dismiss"
                aria-label="Dismiss notification"
                onClick={handleDismiss}
            >
                <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
            </button>
        </div>
    );
}

function ToastHost() {
    const [toasts, setToasts] = useState([]);

    useEffect(() => subscribeToToasts(setToasts), []);

    const { lastJsonMessage } = useWebSocket(process.env.REACT_APP_WS_URL, {
        onOpen: () => {
            console.log('Connected to App WS');
        },
        queryParams: { token: localStorage.getItem('token') },
        onError: (event) => {
            console.error(event);
            pushToast({
                type: 'warning',
                text: 'Live updates disconnected. Reconnecting…',
            });
        },
        shouldReconnect: () => true,
        reconnectInterval: 3000,
    });

    useEffect(() => {
        if (lastJsonMessage?.notification) {
            pushToast(lastJsonMessage.notification);
        }
    }, [lastJsonMessage]);

    if (!toasts.length) return null;

    return ReactDOM.createPortal(
        <div className="app-toast-stack" aria-live="polite">
            {toasts.map((toast) => (
                <ToastItem key={toast.id} toast={toast} onDismiss={dismissToast} />
            ))}
        </div>,
        document.body
    );
}

export default ToastHost;